// A single animation loop for the page. It reads native scroll and eases toward it,
// so trackpads, keyboards and screen readers keep normal scrolling while motion gets weight.

export type FrameState = {
    time: number
    dt: number
    scrollY: number
    rawScrollY: number
    velocity: number
    viewportW: number
    viewportH: number
    pointerX: number
    pointerY: number
    rawPointerX: number
    rawPointerY: number
}

type FrameFn = (state: FrameState) => void

const SCROLL_EASE = 0.12
const POINTER_EASE = 0.08

const subscribers = new Set<FrameFn>()

const state: FrameState = {
    time: 0,
    dt: 0,
    scrollY: 0,
    rawScrollY: 0,
    velocity: 0,
    viewportW: 0,
    viewportH: 0,
    pointerX: 0,
    pointerY: 0,
    rawPointerX: 0,
    rawPointerY: 0
}

let frame = 0
let last = 0
let primed = false
let easing = true

const onPointer = (event: PointerEvent): void => {
    if (!state.viewportW || !state.viewportH) return

    state.rawPointerX = (event.clientX / state.viewportW) * 2 - 1
    state.rawPointerY = (event.clientY / state.viewportH) * 2 - 1
}

const onResize = (): void => {
    state.viewportW = window.innerWidth
    state.viewportH = window.innerHeight
}

const ease = (current: number, target: number, factor: number, dt: number): number => {
    const k = 1 - Math.pow(1 - factor, dt * 60)
    const next = current + (target - current) * k

    return Math.abs(target - next) < 0.05 ? target : next
}

const loop = (now: number): void => {
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 1 / 60
    last = now

    const raw = window.scrollY
    const previous = state.rawScrollY

    state.time = now / 1000
    state.dt = dt
    state.rawScrollY = raw
    state.scrollY = easing ? ease(state.scrollY, raw, SCROLL_EASE, dt) : raw
    state.velocity += ((raw - previous) / dt - state.velocity) * 0.12
    state.pointerX = easing ? ease(state.pointerX, state.rawPointerX, POINTER_EASE, dt) : state.rawPointerX
    state.pointerY = easing ? ease(state.pointerY, state.rawPointerY, POINTER_EASE, dt) : state.rawPointerY

    subscribers.forEach(fn => fn(state))
    frame = window.requestAnimationFrame(loop)
}

const start = (): void => {
    if (frame || typeof window === 'undefined') return

    if (!primed) {
        primed = true
        onResize()
        state.scrollY = window.scrollY
        state.rawScrollY = window.scrollY
        window.addEventListener('pointermove', onPointer, { passive: true })
        window.addEventListener('resize', onResize, { passive: true })
    }

    last = 0
    frame = window.requestAnimationFrame(loop)
}

const stop = (): void => {
    if (!frame) return

    window.cancelAnimationFrame(frame)
    frame = 0
}

export const subscribeFrame = (fn: FrameFn): (() => void) => {
    subscribers.add(fn)
    start()

    return () => {
        subscribers.delete(fn)
        if (subscribers.size === 0) stop()
    }
}

// Reduced motion turns the easing off so everything tracks the real scroll position.
export const setMotionEasing = (enabled: boolean): void => {
    easing = enabled
}

export const getFrameState = (): FrameState => state

export const clamp = (value: number, min = 0, max = 1): number => Math.min(max, Math.max(min, value))

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

// Maps progress inside [start, end] to 0..1.
export const range = (value: number, start: number, end: number): number => clamp((value - start) / (end - start))

export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3)

export const easeInOutCubic = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

// Document-relative top of an element, measured once and cached by the caller.
export const documentTop = (element: HTMLElement): number => element.getBoundingClientRect().top + window.scrollY
